export type ParticipantMergeHighlightParticipant = {
  odrNumber: string;
  firstName: string;
  lastName: string;
  displayName: string;
  dob: string;
  ssn: string;
  cin: string;
  cid: string;
  champid: string;
  fin: string;
  bookings: string[];
  matchedFields: string[];
};

export type MatchHighlightState = 'none' | 'selected' | 'supporting';

export const getParticipantName = (participant: ParticipantMergeHighlightParticipant) => {
  const canonicalName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim();
  if (canonicalName) {
    return canonicalName;
  }

  if (participant.displayName?.trim()) {
    return participant.displayName.trim();
  }

  return 'Unknown';
};

const hasMatchedField = (participant: ParticipantMergeHighlightParticipant, fields: string | string[]) => {
  const fieldList = Array.isArray(fields) ? fields : [fields];
  return fieldList.some((field) => participant.matchedFields.includes(field));
};

const normalizeFieldValue = (value: string | null | undefined) => {
  return String(value || '').trim().toLowerCase();
};

const normalizeFieldDigits = (value: string | null | undefined) => {
  return normalizeFieldValue(value).replace(/\D+/g, '');
};

const normalizeFieldIdentifier = (value: string | null | undefined) => {
  return normalizeFieldValue(value).replace(/[\s.-]+/g, '');
};

const normalizeParticipantMatchField = (field: string) => {
  if (field === 'cid') {
    return 'champid';
  }

  return field;
};

const getParticipantBookingSet = (participant: ParticipantMergeHighlightParticipant) => {
  return new Set(
    (participant.bookings || [])
      .map((booking) => normalizeFieldValue(booking))
      .filter(Boolean)
  );
};

const fieldsMatchBetweenParticipants = (
  participant: ParticipantMergeHighlightParticipant,
  survivor: ParticipantMergeHighlightParticipant,
  field: string
) => {
  const normalizedField = normalizeParticipantMatchField(field);

  if (normalizedField === 'name') {
    const participantName = normalizeFieldValue(getParticipantName(participant));
    const survivorName = normalizeFieldValue(getParticipantName(survivor));
    if (!participantName || !survivorName) {
      return false;
    }

    return participantName === survivorName;
  }

  if (normalizedField === 'dob') {
    const participantDob = normalizeFieldValue(participant.dob);
    const survivorDob = normalizeFieldValue(survivor.dob);
    if (!participantDob || !survivorDob) {
      return false;
    }

    return participantDob === survivorDob;
  }

  if (normalizedField === 'ssn') {
    const participantSsn = normalizeFieldDigits(participant.ssn);
    const survivorSsn = normalizeFieldDigits(survivor.ssn);
    if (!participantSsn || !survivorSsn) {
      return false;
    }

    return participantSsn === survivorSsn;
  }

  if (normalizedField === 'cin') {
    const participantCin = normalizeFieldIdentifier(participant.cin);
    const survivorCin = normalizeFieldIdentifier(survivor.cin);
    if (!participantCin || !survivorCin) {
      return false;
    }

    return participantCin === survivorCin;
  }

  if (normalizedField === 'champid') {
    const participantChampId = normalizeFieldDigits(participant.cid || participant.champid);
    const survivorChampId = normalizeFieldDigits(survivor.cid || survivor.champid);
    if (!participantChampId || !survivorChampId) {
      return false;
    }

    return participantChampId === survivorChampId;
  }

  if (normalizedField === 'fin') {
    const participantFin = normalizeFieldValue(participant.fin);
    const survivorFin = normalizeFieldValue(survivor.fin);
    if (!participantFin || !survivorFin) {
      return false;
    }

    return participantFin === survivorFin;
  }

  if (normalizedField === 'booking') {
    const participantBookings = getParticipantBookingSet(participant);
    if (!participantBookings.size) {
      return false;
    }

    const survivorBookings = getParticipantBookingSet(survivor);
    if (!survivorBookings.size) {
      return false;
    }

    return Array.from(participantBookings).some((booking) => survivorBookings.has(booking));
  }

  return false;
};

export const getMatchHighlightState = (
  participant: ParticipantMergeHighlightParticipant,
  participants: ParticipantMergeHighlightParticipant[],
  fields: string | string[]
): MatchHighlightState => {
  const fieldList = Array.isArray(fields) ? fields : [fields];
  const hasGroupMatch = participants.some((candidate) => {
    if (candidate.odrNumber === participant.odrNumber) {
      return false;
    }

    return fieldList.some((field) => fieldsMatchBetweenParticipants(participant, candidate, field));
  });

  if (!hasGroupMatch) {
    return 'none';
  }

  if (hasMatchedField(participant, fields)) {
    return 'selected';
  }

  return 'supporting';
};

export const getMatchedCellClassName = (matchState: MatchHighlightState) => {
  if (matchState === 'none') {
    return '';
  }

  if (matchState === 'supporting') {
    return 'bg-green-50 font-medium text-[var(--neutral-gray-120)]';
  }

  return 'bg-green-100 font-medium text-[var(--neutral-gray-120)]';
};
